import React, { useState, useCallback, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js';
import config from '../config';
import './PaymentModal.css';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPaymentSuccess?: () => void;
  gameId?: number;
}

const stripePromise = loadStripe(config.stripePublishableKey);

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  onPaymentSuccess,
  gameId
}) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<string>('basic');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentMessage, setPaymentMessage] = useState<string>(
    'Ваш запрос принят игрой. Выберите доступ, чтобы продолжить полную игру.'
  );
  const [callToAction, setCallToAction] = useState<string>(
    'Выбери формат и продолжи:'
  );
  const [isLoadingMessage, setIsLoadingMessage] = useState(false);

  // Персонализированные описания тарифов (обновляются через API)
  const [tariffDescriptions, setTariffDescriptions] = useState({
    basic: 'Достаточно, чтобы закреплять инсайты',
    premium: 'Интенсивная проработка и быстрый прогресс',
    single: 'Чтобы пройти текущий запрос разово'
  });

  const products = [
    {
      code: 'basic',
      name: 'Basic',
      price: 29.00,
      pricePerGame: 14.50,
      period: '/мес',
      description: '2 игры в месяц',
      subtitle: tariffDescriptions.basic,
      chip: 'Популярный',
      chipClass: 'popular',
      discount: '–24%',
      recommended: true
    },
    {
      code: 'premium',
      name: 'Premium',
      price: 69.00,
      pricePerGame: 13.80,
      period: '/мес',
      description: '5 игр в месяц',
      subtitle: tariffDescriptions.premium,
      chip: 'Лучшая цена',
      chipClass: 'best-price',
      discount: '–27%',
      recommended: true
    },
    {
      code: 'single_game',
      name: 'Single',
      price: 19.00,
      pricePerGame: 19.00,
      period: '',
      description: '1 полная игра',
      subtitle: tariffDescriptions.single,
      chip: null,
      chipClass: null,
      discount: null,
      recommended: false
    }
  ];

  // Загружаем персонализированные тексты при открытии модалки с polling
  useEffect(() => {
    if (!isOpen || !gameId) return;

    setIsLoadingMessage(true);
    let pollCount = 0;
    const maxPolls = 15; // Максимум 15 попыток (30 секунд)
    let timeoutId: NodeJS.Timeout;

    const loadPaymentData = async () => {
      try {
        const { authUtils } = await import('../supabase');
        const token = await authUtils.getAccessToken();

        if (!token) {
          setIsLoadingMessage(false);
          return;
        }

        const response = await fetch(`/api/payments/games/${gameId}/payment-message`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();

          // Проверяем что данные действительно персонализированные (не дефолтные)
          const hasPersonalizedData = data.header &&
            data.header !== 'Ваш запрос принят игрой. Выберите доступ, чтобы продолжить полную игру.';

          if (hasPersonalizedData) {
            // Обновляем заголовок
            if (data.header) {
              setPaymentMessage(data.header);
            }

            // Обновляем CTA
            if (data.cta) {
              setCallToAction(data.cta);
            }

            // Обновляем описания тарифов
            if (data.basic || data.premium || data.single) {
              setTariffDescriptions({
                basic: data.basic || tariffDescriptions.basic,
                premium: data.premium || tariffDescriptions.premium,
                single: data.single || tariffDescriptions.single
              });
            }

            setIsLoadingMessage(false);
          } else if (pollCount < maxPolls) {
            // Данные ещё не готовы - продолжаем polling
            pollCount++;
            timeoutId = setTimeout(loadPaymentData, 2000); // Повторяем через 2 секунды
          } else {
            // Достигнут лимит попыток - оставляем дефолтные тексты
            setIsLoadingMessage(false);
          }
        } else {
          setIsLoadingMessage(false);
        }
      } catch (err) {
        console.error('Failed to load payment data:', err);
        setIsLoadingMessage(false);
      }
    };

    loadPaymentData();

    // Cleanup function для отмены pending timeouts
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isOpen, gameId]);

  const handleProductSelect = async (productCode: string) => {
    setSelectedProduct(productCode);
    setLoading(true);
    setError(null);

    // КРИТИЧНО: Открываем popup СИНХРОННО с кликом (ДО async операций)
    // Иначе Safari/Chrome заблокируют popup как spam
    // Без параметров размера - браузер сам выбирает оптимальный размер для устройства
    const paymentWindow = window.open('about:blank', '_blank');

    try {
      const { authUtils } = await import('../supabase');
      const token = await authUtils.getAccessToken();
      if (!token) {
        setError('Пожалуйста, войдите в систему для оплаты');
        setLoading(false);
        // Закрываем popup при ошибке
        if (paymentWindow) paymentWindow.close();
        return;
      }

      const response = await fetch('/api/payments/create-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          product_code: productCode,
          success_url: `${window.location.origin}/payment-success`,
          cancel_url: window.location.href,
          ui_mode: 'hosted'
        })
      });

      if (!response.ok) {
        throw new Error('Payment creation failed');
      }

      const data = await response.json();

      // Перенаправляем УЖЕ открытое окно на Stripe checkout
      if (data.checkout_url && paymentWindow) {
        paymentWindow.location.href = data.checkout_url;

        // Отслеживаем закрытие popup окна
        const checkClosed = setInterval(() => {
          if (paymentWindow.closed) {
            clearInterval(checkClosed);
            // Popup закрылось - закрываем модальное окно и вызываем callback
            setTimeout(() => {
              if (onPaymentSuccess) {
                onPaymentSuccess();
              }
              onClose();
            }, 500); // Небольшая задержка для лучшего UX
          }
        }, 1000);

        // Очищаем интервал через 10 минут на случай если окно не закроется
        setTimeout(() => {
          clearInterval(checkClosed);
        }, 600000);
      }

    } catch (err: any) {
      console.error('Payment error:', err);
      setError('Ошибка при создании платежа. Попробуйте снова.');
      // Закрываем popup при ошибке
      if (paymentWindow) paymentWindow.close();
    } finally {
      setLoading(false);
    }
  };

  const fetchClientSecret = useCallback(async () => {
    return clientSecret!;
  }, [clientSecret]);

  const options = { fetchClientSecret };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  const handlePaymentComplete = () => {
    console.log('Payment completed successfully via GitHub Actions deployment!');
    if (onPaymentSuccess) {
      onPaymentSuccess();
    }
    setClientSecret(null);
    setSelectedProduct('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="payment-modal-overlay" onClick={onClose}>
      <div className="payment-modal-content container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <button className="btn-close position-absolute top-0 end-0 m-3" onClick={onClose} aria-label="Close"></button>

        <div className="text-center mb-4">
          <p className="text-body-secondary mb-0" style={{ fontSize: '16px', lineHeight: '1.6', fontWeight: '500' }}>
            {paymentMessage}
          </p>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {!clientSecret ? (
          <>
            <h5 className="text-center fw-semibold mb-4">
              {callToAction}
            </h5>

            <div className="row row-cols-1 g-3 mb-4">
              {products.map((product) => (
                <div key={product.code} className="col">
                  <div
                    className={`card h-100 position-relative ${selectedProduct === product.code ? 'border-primary shadow-sm' : 'border-secondary'} ${product.code === 'premium' ? 'border-2' : ''}`}
                    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                    onClick={() => !loading && handleProductSelect(product.code)}
                  >
                    <input
                      type="radio"
                      name="product"
                      value={product.code}
                      checked={selectedProduct === product.code}
                      onChange={() => setSelectedProduct(product.code)}
                      className="position-absolute opacity-0"
                      style={{ pointerEvents: 'none' }}
                    />

                    {product.chip && (
                      <div className="position-absolute top-0 start-50 translate-middle">
                        <span className={`badge ${product.chipClass === 'popular' ? 'bg-primary' : 'bg-warning text-dark'} px-3 py-2`}>
                          {product.chip}
                        </span>
                      </div>
                    )}

                    <div className="card-body p-4 d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="d-flex align-items-center gap-2">
                          {product.recommended && (
                            <span style={{ fontSize: '18px' }}>
                              {product.code === 'premium' ? '🟣' : '🟩'}
                            </span>
                          )}
                          <h5 className={`mb-0 ${product.code === 'premium' ? 'text-primary fw-bold' : 'fw-semibold'}`}>
                            {product.name}
                          </h5>
                        </div>

                        <div className="text-end">
                          <div className={`${product.code === 'premium' ? 'text-primary' : 'text-dark'}`} style={{ fontSize: '28px', fontWeight: '800', lineHeight: '1' }}>
                            {formatPrice(product.pricePerGame, 'EUR')}
                            <small className="fs-6 fw-normal">/игру</small>
                          </div>
                          {product.period && (
                            <div className="text-muted" style={{ fontSize: '11px', fontWeight: '400' }}>
                              {formatPrice(product.price, 'EUR')}{product.period}
                            </div>
                          )}
                        </div>
                      </div>

                      <p className="text-body-secondary mb-2" style={{ fontSize: '13px', fontWeight: '500' }}>
                        {product.description}
                        {product.discount && <span className="text-success fw-semibold"> · {product.discount}</span>}
                      </p>

                      <p className="text-body-secondary mb-0 flex-grow-1" style={{ fontSize: '14px', lineHeight: '1.5', fontWeight: '400' }}>
                        {product.subtitle}
                      </p>

                      {loading && selectedProduct === product.code && (
                        <div className="position-absolute top-50 start-50 translate-middle">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Загрузка...</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-4">
              <p className="fw-semibold mb-2" style={{ fontSize: '14px' }}>
                🔒 Безопасная оплата через Stripe
              </p>
              <p className="text-muted mb-0" style={{ fontSize: '12px' }}>
                • Мгновенная активация • Отмена в любое время
              </p>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default PaymentModal;